import { useCallback } from "react";
import { useDispatch } from "react-redux";

import { useQueryClient } from "@tanstack/react-query";

import { apiThatThrows } from "containers/Request";
import { TiVoAppProvider } from "hooks/tivoApps";
import { ApiQueryParams, ApiResponse } from "types/api";

const providerToOption = (app?: TiVoAppProvider | null) => ({
  id: app?.id ?? null,
  value: app?.providerId ?? null,
  label: app?.label ?? "",
  img: app?.assets?.prod?.primaryBrandingLogoSquare?.url ?? null,
});

const mapInitialItem = (data: TiVoAppProvider[] | null) => {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map(providerToOption)[0];
};

const mapData = (data: TiVoAppProvider[]) => {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map(providerToOption);
};

const filteredApps = (data: TiVoAppProvider[], idsList: string[] = []) =>
  data.filter((app) => !idsList.includes(app.id));

export function useApplicationAutocomplete(
  options: {
    excludeIDs?: string[];
    limit?: number;
  } = {}
) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { excludeIDs = [], limit } = options;

  const fetchTiVoProvidersList = useCallback(
    async (query: ApiQueryParams) => {
      return queryClient.fetchQuery<ApiResponse<TiVoAppProvider[]>>({
        queryKey: ["applicationAutoComplete", { limit, ...query }],
        queryFn: async () => {
          return dispatch(
            apiThatThrows.getTiVoProviders.action({
              queryParams: { limit, ...query },
            })
          );
        },
      });
    },
    [dispatch, limit, queryClient]
  );

  const fetchData = useCallback(
    async ({ search, page }: { search: string; page: number }) => {
      const data = await fetchTiVoProvidersList({ label: search, page });

      return {
        meta: {
          count: data?.meta?.total ?? 0,
        },
        results: filteredApps(data.results ?? [], excludeIDs),
      };
    },
    [excludeIDs, fetchTiVoProvidersList]
  );

  const fetchInitialItem = useCallback(
    async (initialValue: string) => {
      return fetchTiVoProvidersList({
        providerId: initialValue,
      });
    },
    [fetchTiVoProvidersList]
  );

  return {
    fetchData,
    fetchInitialItem,
    mapData,
    mapInitialItem,
  };
}
